/** @format */

import React from "react";
import { Link } from "react-router-dom";
import { useUsers } from "../hooks/useUsers";

interface UserProfileLinkProps {
  userId: string;
  className?: string;
  children?: React.ReactNode;
  showAvatar?: boolean;
  avatarSize?: "sm" | "md" | "lg";
  showRole?: boolean;
  fallbackName?: string;
  fallbackAvatarUrl?: string;
}

const UserProfileLink: React.FC<UserProfileLinkProps> = ({
  userId,
  className = "",
  children,
  showAvatar = false,
  avatarSize = "md",
  showRole = false,
  fallbackName,
  fallbackAvatarUrl,
}) => {
  const { getUserDisplayName, getUserAvatar, getUserRole, fetchUsers } = useUsers();
  
  // Ensure the user is fetched on mount so the name isn't Unknown User
  React.useEffect(() => {
    if (userId) {
      fetchUsers([userId]);
    }
  }, [userId, fetchUsers]);
  
  const resolvedName = getUserDisplayName(userId);
  const displayName = resolvedName === 'Unknown User' ? (fallbackName || resolvedName) : resolvedName;
  const avatar = getUserAvatar(userId) || fallbackAvatarUrl || null;
  const role = getUserRole(userId);

  // Avatar size classes
  const avatarSizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  };

  // Default content if no children provided
  const defaultContent = (
    <div className="flex items-center space-x-2">
      {showAvatar && (
        <div className="flex-shrink-0">
          {avatar ? (
            <img
              src={avatar}
              alt={displayName}
              className={`${avatarSizeClasses[avatarSize]} rounded-full object-cover border border-gray-200`}
            />
          ) : (
            <div className={`${avatarSizeClasses[avatarSize]} rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xs font-medium border border-gray-200`}>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}
      
      <div className="flex flex-col">
        <span className="font-medium text-gray-900 hover:text-primary-600 transition-colors">
          {displayName}
        </span>
        {showRole && role && (
          <span className="text-xs text-gray-500 capitalize">
            {role === 'pending' ? 'Pending Author' : role}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Link
      to={`/profile/${userId}`}
      className={`inline-block hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded ${className}`}
      title={`View ${displayName}'s profile`}
    >
      {children || defaultContent}
    </Link>
  );
};

export default UserProfileLink;
